import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  BadgeCheck, ShieldCheck, ShieldX, Hourglass, ShieldQuestion, IdCard,
  UploadCloud, ExternalLink, FileText, Image as ImageIcon, Loader2, Send,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import useAxiosAuth from '../../hooks/useAxiosAuth';
import useTenantFeatures from '../../hooks/useTenantFeatures';
import baseURL from '../../utils/baseURL';

// "GET VERIFIED" — the doctor's side of the open-platform review (issue #5,
// owner 20 Oct 2026). The reviewer's side is pages/DoctorReview.jsx; the twin
// of this card in the Pro-App is src/components/GetVerifiedCard.jsx.
//
// Drawn on the doctor's OWN profile only (the caller checks the role), and
// only where the tenant is the open platform — `isOpenPlatform` from
// useTenantFeatures, which fails CLOSED; GET /doctor-review/me answers
// `{ eligible: false }` for any other tenant and the card then draws nothing,
// so a stale cache can never show it to a hospital's doctor.
//
// The flow: council number + ID proof (image or PDF, 5 MB) → Submit → Pending.
// The proof is uploaded the moment it is picked (POST /doctor-review/id-proof)
// and the key it answers is what Submit names. After a rejection the card
// shows the reviewer's reason and the same form, pre-filled, so the doctor can
// correct it and submit again — keeping the proof on file unless they pick a
// new one.

const ACCEPT = 'image/jpeg,image/png,image/webp,application/pdf';
const ACCEPTED_TYPES = ACCEPT.split(',');
const MAX_BYTES = 5 * 1024 * 1024;
const COUNCIL_MAX = 100;

const STATUS = {
  not_required: { icon: ShieldQuestion, cls: 'bg-gray-100 text-gray-600', key: 'status_not_submitted', fallback: 'Not submitted' },
  pending_review: { icon: Hourglass, cls: 'bg-amber-50 text-amber-700', key: 'status_pending', fallback: 'Pending' },
  approved: { icon: ShieldCheck, cls: 'bg-emerald-50 text-emerald-700', key: 'status_approved', fallback: 'Approved' },
  rejected: { icon: ShieldX, cls: 'bg-rose-50 text-rose-700', key: 'status_rejected', fallback: 'Rejected' },
};

// The server's refusal codes, in the doctor's words.
const ERROR_TEXT = {
  ID_PROOF_TYPE: ['err_type', 'The ID proof must be an image (JPG, PNG, WEBP) or a PDF.'],
  ID_PROOF_TOO_LARGE: ['err_size', 'The ID proof must be 5 MB or smaller.'],
  ID_PROOF_MISSING: ['err_missing', 'Choose an image or a PDF of your ID proof.'],
  ID_PROOF_NOT_YOURS: ['err_not_yours', 'Upload your ID proof again, then submit.'],
  ALREADY_PENDING: ['err_pending', 'Your verification is already being reviewed.'],
  ALREADY_APPROVED: ['err_approved', 'You are already verified.'],
  NOT_OPEN_PLATFORM: ['err_not_open', 'Your organisation lists you itself — no platform verification is needed.'],
};

const formatDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB');
};

const ProofLink = ({ proof, label, testId }) => {
  if (!proof?.url) return null;
  const Icon = proof.type === 'pdf' ? FileText : ImageIcon;
  return (
    <a
      href={proof.url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#2D8A8A] hover:text-[#1a365d]"
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
      <ExternalLink className="w-3 h-3" />
    </a>
  );
};

export default function GetVerifiedCard() {
  const { t } = useTranslation();
  const axiosInstance = useAxiosAuth();
  const { isOpenPlatform } = useTenantFeatures();

  const [state, setState] = useState(null); // GET /me answer
  const [loadError, setLoadError] = useState('');
  const [council, setCouncil] = useState('');
  const [newProof, setNewProof] = useState(null); // { key, proof, name }
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const fileRef = useRef(null);

  const errorText = useCallback((err, fallbackKey, fallback) => {
    const code = err?.response?.data?.code;
    if (code && ERROR_TEXT[code]) return t(`getVerified.${ERROR_TEXT[code][0]}`, ERROR_TEXT[code][1]);
    const msg = err?.response?.data?.message;
    if (typeof msg === 'string' && msg) return msg;
    return t(`getVerified.${fallbackKey}`, fallback);
  }, [t]);

  const load = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get(`${baseURL}/doctor-review/me`);
      setState(data || { eligible: false });
      setCouncil(data?.councilRegistrationNumber || '');
      setNewProof(null);
      setLoadError('');
    } catch (err) {
      setLoadError(errorText(err, 'load_error', 'Could not load your verification.'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isOpenPlatform) load();
  }, [isOpenPlatform, load]);

  // A decision pushed while the profile is open (fcmService dispatches it).
  useEffect(() => {
    if (!isOpenPlatform) return undefined;
    const onUpdate = () => load();
    window.addEventListener('doctor-review-update', onUpdate);
    return () => window.removeEventListener('doctor-review-update', onUpdate);
  }, [isOpenPlatform, load]);

  if (!isOpenPlatform) return null;
  if (!state && !loadError) return null; // first read in flight — no flash
  if (state && state.eligible === false) return null;

  const status = state?.reviewStatus || 'not_required';
  const meta = STATUS[status] || STATUS.not_required;
  const StatusIcon = meta.icon;
  const editable = status === 'not_required' || status === 'rejected';
  const onFileProof = state?.idProof?.uploaded ? state.idProof : null;
  const canSubmit = editable && !uploading && !submitting
    && council.trim().length > 0 && !!(newProof || onFileProof);

  const pickFile = async (event) => {
    const file = event.target.files?.[0];
    // Same file picked twice must still fire onChange.
    if (fileRef.current) fileRef.current.value = '';
    if (!file) return;
    setFormError('');
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setFormError(t('getVerified.err_type', ERROR_TEXT.ID_PROOF_TYPE[1]));
      return;
    }
    if (file.size > MAX_BYTES) {
      setFormError(t('getVerified.err_size', ERROR_TEXT.ID_PROOF_TOO_LARGE[1]));
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await axiosInstance.post(`${baseURL}/doctor-review/id-proof`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setNewProof({ key: data?.idProofKey, proof: data?.idProof || null, name: file.name });
    } catch (err) {
      setFormError(errorText(err, 'upload_error', 'Could not upload the ID proof. Try again.'));
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setFormError('');
    try {
      await axiosInstance.post(`${baseURL}/doctor-review/submit`, {
        councilRegistrationNumber: council.trim(),
        ...(newProof?.key ? { idProofKey: newProof.key } : {}),
      });
      toast.success(t('getVerified.submitted_toast', 'Submitted — the Mitra One Care team will review it.'));
      await load();
    } catch (err) {
      setFormError(errorText(err, 'submit_error', 'Could not submit. Try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="get-verified-card"
      className="mb-6 rounded-2xl border border-[#4ABDBD]/30 bg-gradient-to-br from-[#4ABDBD]/5 to-white p-4 sm:p-5 shadow-sm"
    >
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="p-2.5 bg-gradient-to-br from-[#4ABDBD] to-[#2D8A8A] rounded-xl shadow-sm shrink-0">
            <BadgeCheck className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-base sm:text-lg font-bold text-gray-800">{t('getVerified.title', 'Get verified')}</h3>
            <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
              {t('getVerified.subtitle', 'Mitra One Care checks your medical council registration and ID proof. Approved doctors show a Verified badge to patients.')}
            </p>
          </div>
        </div>
        {state && (
          <span
            data-testid="get-verified-status"
            data-status={status}
            className={`self-start inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${meta.cls}`}
          >
            <StatusIcon className="w-3.5 h-3.5" />
            {t(`getVerified.${meta.key}`, meta.fallback)}
          </span>
        )}
      </div>

      {loadError && !state ? (
        <div className="mt-4 flex items-center gap-3 text-sm text-gray-500">
          <span>{loadError}</span>
          <button type="button" onClick={load} className="text-[#2D8A8A] font-semibold hover:underline">
            {t('getVerified.retry', 'Try again')}
          </button>
        </div>
      ) : null}

      {state && !editable ? (
        <div className="mt-4 space-y-2 text-sm text-gray-700">
          <p data-testid="get-verified-message">
            {status === 'approved'
              ? t('getVerified.approved_text', 'You are verified. Patients see the Verified badge on your profile.')
              : t('getVerified.pending_text', 'Submitted — the Mitra One Care team is reviewing it. You will be told by e-mail and notification.')}
          </p>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs text-gray-500">
            {state.councilRegistrationNumber && (
              <span className="inline-flex items-center gap-1.5">
                <IdCard className="w-3.5 h-3.5 text-gray-400" />
                {t('getVerified.council_label', 'Council registration number')}: <strong className="text-gray-700">{state.councilRegistrationNumber}</strong>
              </span>
            )}
            {status === 'approved' && state.reviewedAt && (
              <span>{t('getVerified.verified_on', 'Verified on {{date}}', { date: formatDate(state.reviewedAt) })}</span>
            )}
            <ProofLink proof={state.idProof} label={t('getVerified.view_proof', 'View ID proof')} testId="get-verified-proof-link" />
          </div>
        </div>
      ) : null}

      {state && editable ? (
        <div className="mt-4 space-y-4">
          {status === 'rejected' && (
            <div data-testid="get-verified-rejected" className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm">
              <p className="font-semibold text-rose-700">{t('getVerified.rejected_title', 'Your verification was not approved.')}</p>
              {state.reviewNote && (
                <p className="mt-1 text-gray-700" data-testid="get-verified-reason">
                  <span className="font-medium">{t('getVerified.reason', 'Reason')}:</span> {state.reviewNote}
                </p>
              )}
              <p className="mt-1 text-gray-600">{t('getVerified.rejected_hint', 'Correct the details below and submit again.')}</p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="block text-sm font-medium text-gray-600 mb-1">
                {t('getVerified.council_label', 'Council registration number')}
              </span>
              <input
                type="text"
                data-testid="get-verified-council-input"
                value={council}
                maxLength={COUNCIL_MAX}
                onChange={(e) => setCouncil(e.target.value.replace(/[<>]/g, ''))}
                placeholder={t('getVerified.council_placeholder', 'e.g. TNMC 123456')}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]/40 focus:border-[#4ABDBD]"
              />
            </label>

            <div>
              <span className="block text-sm font-medium text-gray-600 mb-1">{t('getVerified.proof_label', 'ID proof')}</span>
              <input
                ref={fileRef}
                type="file"
                accept={ACCEPT}
                className="hidden"
                data-testid="get-verified-file-input"
                onChange={pickFile}
              />
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  data-testid="get-verified-upload-button"
                  disabled={uploading || submitting}
                  onClick={() => fileRef.current?.click()}
                  className="inline-flex items-center gap-2 px-3 py-2.5 rounded-xl border border-dashed border-[#4ABDBD] text-sm font-semibold text-[#2D8A8A] bg-white hover:bg-[#4ABDBD]/5 disabled:opacity-50"
                >
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
                  {uploading
                    ? t('getVerified.uploading', 'Uploading…')
                    : (newProof || onFileProof)
                      ? t('getVerified.replace_proof', 'Replace file')
                      : t('getVerified.choose_proof', 'Upload image or PDF')}
                </button>
                {newProof ? (
                  <ProofLink proof={newProof.proof} label={newProof.name || t('getVerified.view_proof', 'View ID proof')} testId="get-verified-proof-link" />
                ) : (
                  <ProofLink proof={onFileProof} label={t('getVerified.current_proof', 'Current ID proof')} testId="get-verified-proof-link" />
                )}
              </div>
              <p className="text-[11px] text-gray-400 mt-1">{t('getVerified.proof_hint', 'Image (JPG, PNG, WEBP) or PDF, up to 5 MB.')}</p>
            </div>
          </div>

          {formError && <p className="text-sm text-rose-600" data-testid="get-verified-error">{formError}</p>}

          <div className="flex justify-end">
            <button
              type="button"
              data-testid="get-verified-submit"
              disabled={!canSubmit}
              onClick={submit}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A] shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {status === 'rejected' ? t('getVerified.resubmit', 'Submit again') : t('getVerified.submit', 'Submit for verification')}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
